import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, apiErr, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmpAvatar } from "@/components/EmpAvatar";
import { PageTitle, SectionCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, MessageSquare, Trophy, Trash2, Send, ImagePlus, X, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Team Player", "Innovation", "Customer Focus", "Leadership", "Going Above & Beyond", "Collaboration", "Excellence", "Custom"];
const MAX_MB = 50;
const REACTIONS = ["👍", "👏", "🎉", "❤️"];

function renderMentions(text) {
  const parts = (text || "").split(/(@[\w][\w.'-]*(?:\s[A-Z][\w.'-]*)*)/g);
  return parts.map((p, i) =>
    p.startsWith("@") ? <span key={i} className="font-medium text-primary">{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

// Facebook-style media grid for a post
function MediaGallery({ media }) {
  const imgs = (media || []).filter((m) => m.type === "image");
  const vids = (media || []).filter((m) => m.type === "video");
  if (!imgs.length && !vids.length) return null;
  return (
    <div className="mt-3 space-y-2">
      {imgs.length > 0 && (
        <div className={`grid gap-1.5 rounded-lg overflow-hidden ${imgs.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid="rec-media-images">
          {imgs.map((m, i) => (
            <a key={i} href={fileUrl(m.file_id)} target="_blank" rel="noreferrer" className={imgs.length === 3 && i === 0 ? "row-span-2" : ""}>
              <img src={fileUrl(m.file_id)} alt="" className="w-full h-full object-cover max-h-96 bg-muted" loading="lazy" />
            </a>
          ))}
        </div>
      )}
      {vids.map((m, i) => (
        <video key={i} src={fileUrl(m.file_id)} controls className="w-full rounded-lg max-h-96 bg-black" data-testid={`rec-media-video-${i}`} />
      ))}
    </div>
  );
}

function CommentBox({ emps, onSubmit, testid }) {
  const [text, setText] = useState("");
  const [menu, setMenu] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const onChange = (val) => {
    setText(val);
    const m = val.match(/@([\w.'-]*)$/);
    if (m) { setQuery(m[1].toLowerCase()); setMenu(true); } else { setMenu(false); }
  };
  const pick = (emp) => {
    setText((t) => t.replace(/@([\w.'-]*)$/, `@${emp.full_name} `));
    setMenu(false);
    inputRef.current?.focus();
  };
  const resolveMentions = (val) => emps.filter((e) => val.includes(`@${e.full_name}`)).map((e) => e.id);
  const submit = () => {
    const t = text.trim();
    if (!t) return;
    onSubmit(t, resolveMentions(text));
    setText(""); setMenu(false);
  };
  const filtered = menu ? emps.filter((e) => e.full_name.toLowerCase().includes(query)).slice(0, 6) : [];

  return (
    <div className="relative mt-3">
      <div className="flex gap-2">
        <Input ref={inputRef} value={text} onChange={(e) => onChange(e.target.value)} placeholder="Add a comment… use @ to mention" className="h-9 rounded-full bg-muted/50" data-testid={`rec-comment-input-${testid}`} onKeyDown={(e) => { if (e.key === "Enter" && !menu) submit(); }} />
        <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={submit} data-testid={`rec-comment-btn-${testid}`}><Send className="h-4 w-4" /></Button>
      </div>
      {menu && filtered.length > 0 && (
        <div className="absolute z-30 bottom-11 left-0 w-64 bg-popover border border-border rounded-md shadow-md py-1 max-h-56 overflow-y-auto" data-testid={`mention-menu-${testid}`}>
          {filtered.map((e) => (
            <button key={e.id} type="button" onClick={() => pick(e)} className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-accent text-left" data-testid={`mention-opt-${e.id}`}>
              <EmpAvatar emp={e} size="h-6 w-6" /> {e.full_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Admin-only Facebook-style composer with drag & drop media
function Composer({ user, onPosted }) {
  const [form, setForm] = useState({ message: "" });
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const hasVideo = media.some((m) => m.type === "video");
    setUploading(true);
    try {
      for (const file of list) {
        const isImg = file.type.startsWith("image/");
        const isVid = file.type.startsWith("video/");
        if (!isImg && !isVid) { toast.error(`${file.name}: only images and videos`); continue; }
        if (file.size > MAX_MB * 1024 * 1024) { toast.error(`${file.name} exceeds ${MAX_MB}MB`); continue; }
        if (isVid && (hasVideo || media.some((m) => m.type === "video"))) { toast.error("Only one video per post"); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((prev) => [...prev, { file_id: data.id, type: isVid ? "video" : "image", content_type: data.content_type, filename: data.filename }]);
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); };
  const removeMedia = (i) => setMedia((prev) => prev.filter((_, k) => k !== i));

  const submit = async () => {
    if (!form.message.trim() && media.length === 0) { toast.error("Write something or add a photo/video"); return; }
    try {
      await api.post("/recognitions", { recipient_id: null, recognition_type: "", message: form.message, public: true, points: 0, media });
      toast.success("Posted 🎉");
      setForm({ message: "" });
      setMedia([]);
      onPosted();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="p-4 rounded-xl border-border mb-4" data-testid="rec-composer" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
      <div className="flex gap-3">
        <EmpAvatar emp={{ full_name: user?.name }} size="h-10 w-10" />
        <div className="flex-1">
          <Textarea maxLength={500} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Share a recognition or shout-out for the team…" className="border-0 bg-muted/50 rounded-xl resize-none focus-visible:ring-1" rows={2} data-testid="rec-message" />

          {media.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid="rec-media-preview">
              {media.map((m, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden border border-border aspect-square bg-muted">
                  {m.type === "image"
                    ? <img src={fileUrl(m.file_id)} alt="" className="w-full h-full object-cover" />
                    : <video src={fileUrl(m.file_id)} className="w-full h-full object-cover" />}
                  {m.type === "video" && <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">VIDEO</span>}
                  <button type="button" onClick={() => removeMedia(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black" data-testid={`rec-media-rm-${i}`}><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}

          <div
            onClick={() => fileRef.current?.click()}
            className={`mt-3 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed py-3 text-sm cursor-pointer transition-colors ${drag ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:bg-muted/50"}`}
            data-testid="rec-dropzone"
          >
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><ImagePlus className="h-4 w-4" /> Drag & drop or click to add photos / video</>}
          </div>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="rec-file-input" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />

          <div className="flex items-center justify-end mt-3">
            <Button onClick={submit} disabled={uploading} data-testid="rec-submit">Post Recognition</Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Recognition() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [board, setBoard] = useState([]);
  const [emps, setEmps] = useState([]);
  const [tab, setTab] = useState("feed");
  const [period, setPeriod] = useState("month");
  const [myEmpId, setMyEmpId] = useState(null);
  const [badges, setBadges] = useState({ points: 0, count: 0, badges: [] });
  useEffect(() => { api.get("/employees/me").then((r) => setMyEmpId(r.data.id)).catch(() => {}); }, []);
  useEffect(() => { api.get("/recognitions/badges").then((r) => setBadges(r.data)).catch(() => {}); }, [items]);

  const load = useCallback(() => {
    api.get(`/recognitions?scope=${tab}`).then((r) => setItems(r.data)).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/recognitions/leaderboard?period=${period}`).then((r) => setBoard(r.data)).catch(() => {}); }, [period]);
  useEffect(() => { api.get("/employees?status=Active").then((r) => setEmps(r.data)).catch(() => {}); }, []);

  const react = async (id, emoji) => {
    try { await api.post(`/recognitions/${id}/react`, { emoji }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addComment = async (id, text, mentions) => {
    if (!text) return;
    try {
      await api.post(`/recognitions/${id}/comment`, { text, mentions: mentions || [] });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (window.confirm("Delete this recognition?")) { await api.delete(`/recognitions/${id}`); load(); } };

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Engagement" title="Recognition" />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex gap-1 rounded-md border border-border p-0.5 w-fit mb-4">
            {["feed", "received", "sent"].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 py-1 text-sm rounded capitalize ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`rec-tab-${t}`}>{t}</button>
            ))}
          </div>

          {isAdmin
            ? <Composer emps={emps} user={user} onPosted={load} />
            : (
              <Card className="p-4 rounded-xl border-border mb-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid="rec-employee-note">
                <Info className="h-4 w-4 shrink-0" /> Recognition points are awarded by HR. You can like and comment on any recognition below.
              </Card>
            )}

          <div className="space-y-4">
            {items.length === 0 && <p className="text-muted-foreground text-center py-12">No recognitions yet.</p>}
            {items.map((r, i) => (
              <Card key={r.id} className="p-5 rounded-xl border-border animate-fade-up" style={{ animationDelay: `${i * 30}ms` }} data-testid={`rec-card-${r.id}`}>
                <div className="flex items-start gap-3">
                  <EmpAvatar emp={{ full_name: r.sender_name, photo_file_id: r.sender_photo_file_id }} size="h-10 w-10" />
                  <div className="flex-1 min-w-0">
                    {r.recipient_id
                      ? <p className="text-sm flex items-center gap-1.5 flex-wrap"><span className="font-semibold">{r.sender_name}</span> <span className="text-muted-foreground">recognized</span> <EmpAvatar emp={{ full_name: r.recipient_name, photo_file_id: r.recipient_photo_file_id }} size="h-5 w-5" /> <span className="font-semibold">{r.recipient_name}</span></p>
                      : <p className="text-sm"><span className="font-semibold">{r.sender_name}</span></p>}
                    {r.recognition_type && <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-medium"><Award className="h-3 w-3" /> {r.recognition_type}</span>}
                    {r.points > 0 && <span className="inline-flex items-center gap-1 mt-1 ml-1.5 px-2 py-0.5 rounded bg-warning/10 text-warning text-xs font-medium" data-testid={`rec-points-${r.id}`}>+{r.points} pts</span>}
                    {r.message && <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">{r.message}</p>}
                    <MediaGallery media={r.media} />
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <div className="flex items-center gap-1">
                        {REACTIONS.map((emo) => {
                          const users = (r.reactions && r.reactions[emo]) || [];
                          const mine = users.includes(user.id);
                          return (
                            <button key={emo} onClick={() => react(r.id, emo)} data-testid={`rec-react-${r.id}-${emo}`}
                              className={`inline-flex items-center gap-1 text-sm px-2 py-0.5 rounded-full border transition-colors ${mine ? "border-primary bg-primary/10 text-primary" : "border-transparent text-muted-foreground hover:bg-muted"}`}>
                              <span className="text-base leading-none">{emo}</span>{users.length > 0 && <span className="font-mono-num text-xs">{users.length}</span>}
                            </button>
                          );
                        })}
                      </div>
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground ml-1"><MessageSquare className="h-4 w-4" /> {(r.comments || []).length}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{fmtDate(r.created_at)}</span>
                      {(user.role === "admin" || r.sender_id === myEmpId) && <button onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`rec-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                    {(r.comments || []).length > 0 && (
                      <div className="mt-3 space-y-2 border-t border-border pt-3">
                        {r.comments.map((c) => <p key={c.id} className="text-sm"><span className="font-medium">{c.name}:</span> <span className="text-muted-foreground">{renderMentions(c.text)}</span></p>)}
                      </div>
                    )}
                    <CommentBox emps={emps} testid={r.id} onSubmit={(text, mentions) => addComment(r.id, text, mentions)} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <SectionCard title="My Recognition" className="mb-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-md bg-warning/10 flex items-center justify-center"><Award className="h-6 w-6 text-warning" /></div>
              <div>
                <p className="font-heading font-extrabold text-2xl font-mono-num">{badges.points} <span className="text-sm text-muted-foreground font-normal">pts</span></p>
                <p className="text-xs text-muted-foreground">{badges.count} recognition(s) received</p>
              </div>
            </div>
            {badges.badges.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="my-badges">
                {badges.badges.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-warning/10 text-warning border border-warning/30 text-xs font-medium"><Trophy className="h-3 w-3" /> {b}</span>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground mt-3">Earn badges: 3 = Rising Star, 10 = Team Favorite, 25 = Legend</p>}
          </SectionCard>
          <SectionCard title="Leaderboard" action={
            <Select value={period} onValueChange={setPeriod}><SelectTrigger className="h-8 w-28" data-testid="board-period"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover"><SelectItem value="month">This Month</SelectItem><SelectItem value="quarter">Quarter</SelectItem><SelectItem value="year">Year</SelectItem></SelectContent></Select>
          }>
            <div className="space-y-3">
              {board.length === 0 && <p className="text-sm text-muted-foreground">No recognitions this period.</p>}
              {board.map((b, i) => (
                <div key={b.employee_id} className="flex items-center gap-3">
                  <span className={`h-7 w-7 rounded-md flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-warning text-warning-foreground" : "bg-muted text-muted-foreground"}`}>{i === 0 ? <Trophy className="h-4 w-4" /> : i + 1}</span>
                  <span className="flex-1 text-sm font-medium truncate">{b.name}</span>
                  <span className="font-mono-num font-bold">{b.points || 0}<span className="text-xs text-muted-foreground font-normal ml-0.5">pts</span></span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
